import axiosInstance from 'services/axiosInstance';
import * as S from './styles';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from "react-hook-form";
//components
import LayoutModal from '../../../../components/LayoutModal';
import FormsCustomer from '../FormsCustomer';
import FormsAddress from '../FormsAddress';
import ProgressBar from "../../../ProgressBar";
import Loading from '../../../../components/Loading';
//hooks
import useLoading from '../../../../hooks/useLoading';
import apiRequest from '../../../../hooks/apiRequest';
//interfaces
import { Inputs, ICustomer } from 'Interfaces/ICustomerAddress';
import { IAgreement } from 'Interfaces/IAgreement';

interface IProps {
    isOpenModal: boolean,
    setIsOpenModal: () => void,
    setToastOpen: (value: boolean) => void;
    setToastData: (data: { status: number; message: string }) => void;
    refreshData: () => void;
    selectedUrlId: number | null;
}

export default function LayoutUpdate({
    isOpenModal,
    setIsOpenModal,
    setToastOpen,
    setToastData,
    refreshData,
    selectedUrlId
}: IProps) {
    const {
        register,
        control,
        reset,
        setValue,
        handleSubmit: handleFormSubmit,
        formState: { errors },
    } = useForm<Inputs>();

    const [isOpensCustomer, setIsOpensCustomer] = useState(true);
    const [addressId, setAddressId] = useState<number | null>(null);
    const [customerID, setCustomerID] = useState<number | null>();
    const { isLoading, startLoading, stopLoading } = useLoading();

    const textInformation = isOpensCustomer ? {
        title: 'Atualizar Cliente',
        subTitle: 'Atualize as informações do cliente em sua base de dados.'
    } : {
        title: 'Atualizar Endereço',
        subTitle: 'Atualize as informações do endereço em sua base de dados.'
    }

    const [agreements, setAgreements] = useState<IAgreement[]>([]);

    const fetchData = async () => {
        startLoading();
        try {
            const [response, responseAgree] = await Promise.all([
                axiosInstance.get(`customer/${selectedUrlId}`),
                axiosInstance.get('agreement')
            ]);
            console.log('response customer update: ', response);
            setAddressId(response.data.address.id);
            setAgreements(responseAgree.data);
            const customerData = response.data;

            // Preenche os dados do cliente
            for (const key in customerData) {
                if (key !== 'address') {
                    setValue(key as keyof Inputs, customerData[key]);
                }
            }

            // Preenche os dados do endereço (se existirem)
            if (customerData.address) {
                for (const key in customerData.address) {
                    setValue(key as keyof Inputs, customerData.address[key]);
                }
            }

        } catch (error) {
            console.log("Error: ", error);
        } finally {
            stopLoading();
        }
    };

    useEffect(() => {
        if (isOpenModal && selectedUrlId !== null) {
            fetchData();
        }
    }, [isOpenModal, selectedUrlId]);

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
        startLoading();
        try {
            // Etapa 1: Avançar para o formulário de endereço
            if (isOpensCustomer) {
                setIsOpensCustomer(false);
                stopLoading();
                return;
            }

            // Etapa 2: Salva os dados
            let customer_id = customerID || null;
            if (!customer_id) {
                // 1. Montar os dados do cliente
                const customerPayload = {
                    full_name: data.full_name.toUpperCase(),
                    cpf: data.cpf,
                    rg: data.rg,
                    birth_date: data.birth_date,
                    phone_primary: data.phone_primary,
                    email: data.email,
                    agreement_id: data.agreement_id,
                    number_agreement: data.number_agreement,
                    flag: data.flag,
                };

                const customerResponse = await apiRequest<ICustomer>(`customer/${selectedUrlId}`, 'PATCH', customerPayload);
                customer_id = customerResponse.data.id;
                setCustomerID(customer_id);
                await finalizeData(customer_id, data);
            } else {
                await finalizeData(customer_id, data);
            }
        } catch (error: any) {
            setToastData({
                status: error?.response?.status || 500,
                message: error.message || 'Erro inesperado',
            });
            setToastOpen(true);
        } finally {
            stopLoading();
        }
    };

    const finalizeData = async (id: number, data: any) => {
        try {
            // Verifica se algum campo de endereço foi preenchido
            const hasAddress =
                data.cep?.trim() ||
                data.street?.trim() ||
                data.neighborhood?.trim() ||
                data.number?.trim() ||
                data.city?.trim() ||
                data.uf?.trim() ||
                data.complement?.trim() ||
                data.reference?.trim();

            if (hasAddress && id) {
                const addressPayload = {
                    customer_id: id,
                    cep: data.cep,
                    street: data.street,
                    neighborhood: data.neighborhood,
                    number: data.number,
                    city: data.city,
                    uf: data.uf,
                    complement: data.complement,
                    reference: data.reference,
                };

                if (addressId) {
                    const addressResponse = await apiRequest(`address/${addressId}`, 'PATCH', addressPayload);

                    if (addressResponse.status === 200) {
                        setToastData({
                            status: 200,
                            message: 'Atualizados com sucesso!',
                        });
                    }
                } else {
                    const addressResponse = await apiRequest('address', 'POST', addressPayload);

                    if (addressResponse.status === 201) {
                        setToastData({
                            status: 201,
                            message: 'Atualizados com sucesso!',
                        });
                    }
                }
            } else {
                setToastData({
                    status: 201,
                    message: 'Atualizado com sucesso!',
                });
            }

            setToastOpen(true);
            refreshData();
            onClose();

        } catch (error: any) {
            setToastData({
                status: error?.response?.status || error?.erros || 500,
                message: error.message || 'Erro inesperado',
            });
            setToastOpen(true);
            console.log('error: ', error);
        } finally {
            stopLoading();
        }
    }

    const onClose = () => {
        reset();
        setIsOpensCustomer(true);
        setIsOpenModal(); // fecha o modal
        setCustomerID(null);
    }

    if (isOpenModal) {
        return (
            <LayoutModal
                statusLoad={isLoading}
                title={textInformation.title}
                subTitle={textInformation.subTitle}
                setModalClose={onClose}
                submiteModal={handleFormSubmit(onSubmit)}
                nameSubmit={isOpensCustomer ? "Próximo" : "Finalizar"}
                nameCancel={"Cancelar"}
                nameIconMinimize={'Registrar Cliente'}
                buttonBack={!isOpensCustomer}
                setModalBack={() => setIsOpensCustomer(!isOpensCustomer)}
                setMaximizeModal={fetchData}
            >
                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        <S.ContentProgress>
                            <ProgressBar name="Cliente" active={isOpensCustomer} />
                            <ProgressBar name="Endereço" active={!isOpensCustomer} />
                        </S.ContentProgress>
                        {
                            isOpensCustomer ?
                                <FormsCustomer
                                    register={register}
                                    control={control}
                                    errors={errors}
                                    agreements={agreements}
                                />
                                :
                                <FormsAddress
                                    setValue={setValue}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                />
                        }
                    </>

                )}

            </LayoutModal>
        )
    }
    return null;
}